import React, { useState } from 'react';
import { X, Trash2, AlertTriangle } from 'lucide-react';
import { deleteRegistry } from '../lib/admin';
import type { RegistryStats } from '../types/admin';

interface Props {
  registry: RegistryStats;
  onClose: () => void;
  onDelete: () => void;
}

export default function DeleteRegistryModal({ registry, onClose, onDelete }: Props) {
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const expectedConfirmText = 'DELETE';
  const isConfirmValid = confirmText === expectedConfirmText;

  const handleDelete = async () => {
    if (!isConfirmValid) {
      setError('Please type DELETE to confirm');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await deleteRegistry(registry.registry_id, reason);
      onDelete();
      onClose();
    } catch (err) {
      console.error('Error deleting registry:', err);
      setError('Failed to delete registry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800/95 backdrop-blur-sm rounded-lg p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-6 h-6 text-red-400" />
            <h3 className="text-lg font-semibold text-white">Delete Registry</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-900/50 text-red-200 p-3 rounded-md">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* Warning */}
          <div className="bg-red-900/30 border border-red-800 p-4 rounded-lg">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-red-200 mb-2">Warning: This action cannot be undone!</h4>
                <p className="text-red-300 text-sm">
                  Deleting this registry will permanently remove:
                </p>
                <ul className="text-red-300 text-sm mt-2 space-y-1 list-disc list-inside">
                  <li>All registry items and gift claims</li>
                  <li>All RSVP responses and events</li>
                  <li>All withdrawal requests and financial data</li>
                  <li>All associated notifications and thank you notes</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Registry Details */}
          <div className="bg-gray-900/50 p-4 rounded-lg">
            <h4 className="font-medium text-white mb-2">Registry to Delete</h4>
            <div className="space-y-2 text-sm">
              <p><span className="text-gray-400">Title:</span> <span className="text-white">{registry.title}</span></p>
              <p><span className="text-gray-400">Owner:</span> <span className="text-white">{registry.owner_name}</span></p>
              <p><span className="text-gray-400">Email:</span> <span className="text-white">{registry.owner_email}</span></p>
              <p><span className="text-gray-400">Total Collected:</span> <span className="text-white">LKR {registry.total_collected.toFixed(2)}</span></p>
              {registry.pending_requests_count > 0 && (
                <p><span className="text-gray-400">Pending Requests:</span> <span className="text-yellow-400">{registry.pending_requests_count} (LKR {registry.pending_requests_amount.toFixed(2)})</span></p>
              )}
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Reason for Deletion (Optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-red-500 focus:border-red-500"
              rows={3}
              placeholder="Enter reason for deleting this registry..."
            />
          </div>

          {/* Confirmation */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Type <span className="font-bold text-red-400">DELETE</span> to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-red-500 focus:border-red-500"
              placeholder="DELETE"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-300 hover:text-white disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={loading || !isConfirmValid}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              <span>{loading ? 'Deleting...' : 'Delete Registry'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}